if (!customElements.get('kaching-custom-display')) {
  class KachingCustomDisplay extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.productTitle = this.dataset.productTitle || '';
      this.productType = this.dataset.productType || 'capsules';
      this.i18n = {
        unavailable: this.dataset.i18nUnavailable || 'Unavailable',
        savingsPrefix: this.dataset.i18nSavingsPrefix || 'Save {{ amount }}',
        dosageSupply: this.dataset.i18nDosageSupply || '{{ months }}-month supply ({{ amount }} {{ unit }})',
        dosageUnitCapsules: this.dataset.i18nDosageUnitCapsules || 'capsules',
        dosageUnitPowder: this.dataset.i18nDosageUnitPowder || 'g',
        pricePerDay: this.dataset.i18nPricePerDay || '{{ amount }} / day',
      };
      this.giftsInTier = this.dataset.giftsInTier === '1';
      this.capsulesPerPackage = parseFloat(this.dataset.capsulesPerPackage) || 0;
      this.capsulesPerDayBySize = (this.dataset.capsulesPerDayBySize || '')
        .split(',')
        .map((value) => parseFloat(value.trim()))
        .filter((value) => !Number.isNaN(value));

      this.headerContainer = this.querySelector('[data-header-container]');
      this.headerTitle = this.querySelector('[data-header-title]');
      this.savingsBadge = this.querySelector('[data-savings-badge]');
      this.savingsBadgeText = this.querySelector('[data-savings-badge-text]');
      this.headerPriceCurrent = this.querySelector('[data-header-price-current]');
      this.headerPriceCompare = this.querySelector('[data-header-price-compare]');
      this.tilesContainer = this.querySelector('[data-tiles-container]');
      this.giftsContainer = this.querySelector('[data-gifts-container]');
      this.giftsWrapper = this.giftsContainer
        ? this.giftsContainer.closest('.kaching-custom-display__gifts')
        : null;

      this.kachingEl = document.querySelector('kaching-bundle');
      this.variantRoot = document.querySelector(`#variant-selects-${this.sectionId}`);
      this.variantJsonEl = this.variantRoot
        ? this.variantRoot.querySelector('script[type="application/json"]')
        : null;
      this.variants = [];
      try {
        this.variants = this.variantJsonEl ? JSON.parse(this.variantJsonEl.textContent) : [];
      } catch (error) {
        this.variants = [];
      }

      if (!this.kachingEl) {
        this.renderError('Kaching Bundles app block not found on this page.');
        return;
      }

      this.dealBars = this.readDealBars();
      if (this.dealBars.length === 0) {
        this.renderError('No Kaching deal configured for this product yet.');
        return;
      }

      this.gifts = this.collectGifts();
      this.selectedDealBarId = this.preselectedDealBarId;

      this.sizeRadios = this.variantRoot
        ? Array.from(this.variantRoot.querySelectorAll('input[type="radio"]'))
        : [];
      this.onSizeChange = this.onSizeChange.bind(this);
      this.sizeRadios.forEach((radio) => radio.addEventListener('change', this.onSizeChange));

      // Everything needed to render (dealBars, gifts, variant prices) is
      // already in the page synchronously - no need to wait on the live
      // Kaching widget just to paint. It's only touched at add-to-cart time.
      this.refresh();
    }

    disconnectedCallback() {
      this.sizeRadios.forEach((radio) => radio.removeEventListener('change', this.onSizeChange));
    }

    // The real rendered widget (verified via devtools) is a child
    // <kaching-bundles-block> custom element that Kaching inserts inside
    // our hidden <kaching-bundle> after its own JS runs - it isn't present
    // at page load. There's no .pricing()/.items()/.quantity JS API on this
    // build; selection happens via plain <input type="radio"> elements
    // inside it, one per deal bar (data-deal-bar-id="<tier.id>").
    waitForKachingBlock() {
      const existing = document.querySelector('kaching-bundles-block');
      if (existing) return Promise.resolve(existing);

      return new Promise((resolve) => {
        const observer = new MutationObserver(() => {
          const el = document.querySelector('kaching-bundles-block');
          if (el) {
            observer.disconnect();
            resolve(el);
          }
        });
        observer.observe(document.body, { childList: true, subtree: true });
        setTimeout(() => {
          observer.disconnect();
          resolve(document.querySelector('kaching-bundles-block'));
        }, 3000);
      });
    }

    readDealSettings() {
      const productId = this.kachingEl.getAttribute('product-id');
      const settingsEls = Array.from(
        document.querySelectorAll(
          `script.kaching-bundles-deal-block-settings[data-product-id="${productId}"]`
        )
      );
      if (!settingsEls.length) return null;
      const parse = (el) => {
        try {
          return JSON.parse(el.textContent);
        } catch (error) {
          return null;
        }
      };
      // Kaching embeds every deal for the product on every market's page
      // (each with marketId / excludedMarketIds). Only on the Czech market
      // pick the deal bound to it; everywhere else keep the first one.
      const CZECH_MARKET_ID = 103847461189;
      if (Number(this.dataset.marketId) === CZECH_MARKET_ID) {
        const czech = settingsEls
          .map(parse)
          .find((settings) => settings && Number(settings.marketId) === CZECH_MARKET_ID);
        if (czech) return czech;
      }
      return parse(settingsEls[0]);
    }

    // Kaching's own multi-language deal text (translated natively in the
    // Kaching app) lives in a separate static <script id="kaching-bundles-
    // translations"> blob - a list of { locale, translations: { dealBlocks:
    // { <dealId>: { "<original text>": "<translated text>" } } } }. The
    // static deal-settings blob we read in readDealSettings() is only ever
    // in the deal's original/default language, so any text sourced from it
    // (e.g. the progressive-gifts title) needs a lookup here to actually
    // show translated on non-default-locale storefronts. The same source
    // string maps to the same translation across every dealBlock entry, so
    // there's no need to match this page's specific deal - the first hit
    // for the current locale is used.
    translateFromKaching(sourceText) {
      if (!sourceText) return null;

      if (this._kachingTranslations === undefined) {
        const el = document.getElementById('kaching-bundles-translations');
        try {
          this._kachingTranslations = el ? JSON.parse(el.textContent) : null;
        } catch (error) {
          this._kachingTranslations = null;
        }
      }

      if (!Array.isArray(this._kachingTranslations)) return null;

      const locale = (document.documentElement.lang || '').split('-')[0].toLowerCase();
      const entry = this._kachingTranslations.find((item) => item.locale === locale);
      const dealBlocks = entry && entry.translations && entry.translations.dealBlocks;
      if (!dealBlocks) return null;

      for (const blockId in dealBlocks) {
        if (Object.prototype.hasOwnProperty.call(dealBlocks[blockId], sourceText)) {
          return dealBlocks[blockId][sourceText];
        }
      }
      return null;
    }

    readDealBars() {
      const settings = this.readDealSettings();
      if (!settings) return [];
      this.preselectedDealBarId = settings.preselectedDealBarId || null;
      return Array.isArray(settings.dealBars) ? settings.dealBars : [];
    }

    getSelectedTier() {
      return (
        this.dealBars.find((tier) => tier.id === this.selectedDealBarId) ||
        this.dealBars.find((tier) => tier.id === this.preselectedDealBarId) ||
        this.dealBars[0]
      );
    }

    getSelectedBarPosition() {
      const tier = this.getSelectedTier();
      const index = this.dealBars.findIndex((bar) => bar.id === (tier && tier.id));
      return index === -1 ? 1 : index + 1;
    }

    resolveGiftDisplay(gift) {
      if (gift.giftType === 'shipping') {
        return { title: this.translateFromKaching(gift.title) || gift.title, image: null, icon: 'local_shipping' };
      }
      if (gift.productGID) {
        const numericId = gift.productGID.split('/').pop();
        const productEl = document.querySelector(
          `script.kaching-bundles-product[data-product-id="${numericId}"]`
        );
        if (productEl) {
          try {
            const productData = JSON.parse(productEl.textContent);
            return { title: productData.title, image: productData.image, icon: 'redeem' };
          } catch (error) {
            // fall through to default below
          }
        }
      }
      const fallbackTitle = gift.title === '{{product}}' ? 'Gift' : gift.title;
      return { title: this.translateFromKaching(fallbackTitle) || fallbackTitle, image: null, icon: 'redeem' };
    }

    collectGifts() {
      const settings = this.readDealSettings();
      this.giftsTitle = null;
      this.hideLockedGifts = false;

      if (!settings || !settings.progressiveGiftsEnabled || !settings.progressiveGifts) {
        return [];
      }

      this.giftsTitle = settings.progressiveGifts.title || null;
      this.hideLockedGifts = !!settings.progressiveGifts.hideLockedGifts;

      const rawGifts = Array.isArray(settings.progressiveGifts.gifts)
        ? settings.progressiveGifts.gifts
        : [];

      return rawGifts.map((gift) => {
        const display = this.resolveGiftDisplay(gift);
        return {
          id: gift.id,
          title: display.title,
          image: display.image,
          icon: display.icon,
          comparePrice: gift.comparePrice || null,
          lockedTitle: this.translateFromKaching(gift.lockedTitle) || gift.lockedTitle,
          unlockAtBar: gift.unlockAtBar,
        };
      });
    }

    getSelectedSizeIndex() {
      const index = this.sizeRadios.findIndex((radio) => radio.checked);
      return index === -1 ? 0 : index;
    }

    getSelectedVariant() {
      const checked = this.sizeRadios.find((radio) => radio.checked);
      if (!checked) return this.variants[0];
      return this.variants.find((variant) => variant.option1 === checked.value) || this.variants[0];
    }

    onSizeChange() {
      // Kaching syncs itself to the native variant picker (confirmed by
      // disableVariantOptionSync: false in its deal config) - no need to
      // push the selection into it ourselves.
      this.refresh();
    }

    getBasePrice(variant) {
      if (!variant) return null;
      return Number(variant.price) / 100 || Number(variant.price);
    }

    // discountType is "default" (no discount), "percentage" (off the
    // quantity x base-price total), or "specific" (discountValue IS the
    // exact final total price for the tier, confirmed against Kaching's
    // own rendered per-item price: e.g. discountValue 69.8 for a 2x tier
    // renders as "€34,90" per item, i.e. 69.8 / 2).
    computeFallbackPrice(tier, variant) {
      const basePrice = this.getBasePrice(variant);
      if (basePrice == null) return null;
      if (tier.discountType === 'specific') {
        return tier.discountValue;
      }
      const total = basePrice * tier.quantity;
      if (tier.discountType === 'percentage') {
        return total * (1 - (tier.discountValue || 0) / 100);
      }
      return total;
    }

    computeDosage(tier, sizeIndex) {
      if (this.capsulesPerPackage <= 0) return null;
      const capsulesPerDay = this.capsulesPerDayBySize[sizeIndex];
      if (!capsulesPerDay) return null;

      const totalCapsules = this.capsulesPerPackage * tier.quantity;
      const daysSupply = totalCapsules / capsulesPerDay;
      const monthsRaw = Math.round((daysSupply / 30) * 10) / 10;
      const monthsSupply = Number.isInteger(monthsRaw)
        ? String(monthsRaw)
        : monthsRaw.toFixed(1).replace('.', ',');

      return { totalCapsules: Math.round(totalCapsules), daysSupply, monthsSupply };
    }

    refresh() {
      const sizeIndex = this.getSelectedSizeIndex();
      const variant = this.getSelectedVariant();
      const available = variant ? variant.available !== false : true;
      const basePrice = this.getBasePrice(variant);

      const tilesData = this.dealBars.map((tier) => ({
        tier,
        imageUrl: tier.kachingImageUrl || null,
        price: this.computeFallbackPrice(tier, variant),
        comparePrice: basePrice != null ? basePrice * tier.quantity : null,
        valid: available,
        validationMessage: available ? null : this.i18n.unavailable,
        dosage: this.computeDosage(tier, sizeIndex),
      }));

      this.renderHeader(tilesData);
      this.renderTiles(tilesData);
      this.renderGifts();

      // computeFallbackPrice re-implements Kaching's discount math just to
      // paint instantly - it WILL go stale the next time the deal's
      // discount type/value changes in Kaching admin. Once Kaching's own
      // widget has actually rendered, correct the displayed price to
      // whatever IT shows - that's authoritative and survives any future
      // change in Kaching, since we're reading its output, not guessing
      // its formula.
      this.correctPricesFromRealWidget(tilesData);
    }

    parseMoneyText(text) {
      if (!text) return null;
      const cleaned = text.replace(/[^\d.,-]/g, '').trim();
      if (!cleaned) return null;
      const normalized = cleaned.includes(',') && !cleaned.includes('.')
        ? cleaned.replace(',', '.')
        : cleaned.replace(/,(?=\d{3}\b)/g, '');
      const value = parseFloat(normalized);
      return Number.isNaN(value) ? null : value;
    }

    async correctPricesFromRealWidget(tilesData, attempt = 1) {
      const kachingBlock = await this.waitForKachingBlock();
      if (!kachingBlock) return;

      let changed = false;
      let anyZero = false;
      tilesData.forEach((data) => {
        const barEl = kachingBlock.querySelector(`[data-deal-bar-id="${data.tier.id}"]`);

        // Extract image from Kaching's rendered bar element and cache on the tier
        // so it survives across refresh() calls (e.g. size changes).
        const imgEl = barEl ? barEl.querySelector('img') : null;
        if (imgEl && imgEl.src && data.tier.kachingImageUrl !== imgEl.src) {
          data.tier.kachingImageUrl = imgEl.src;
          data.imageUrl = imgEl.src;
          changed = true;
        }

        const priceEl = barEl ? barEl.querySelector('.kaching-bundles__bar-price') : null;
        const perItemPrice = priceEl ? this.parseMoneyText(priceEl.textContent) : null;
        // Kaching's block can briefly render before it has resolved a
        // variant/price (shows 0,00 for an instant) - never trust that as
        // a real correction, keep our fallback estimate until it settles.
        if (perItemPrice == null || perItemPrice <= 0) {
          anyZero = true;
          return;
        }

        const total = perItemPrice * data.tier.quantity;
        if (Math.abs(total - data.price) > 0.005) {
          data.price = total;
          changed = true;
        }
      });

      if (changed) {
        this.renderHeader(tilesData);
        this.renderTiles(tilesData);
      }

      // It can take a moment for Kaching to resolve the initial variant -
      // retry a couple of times so the real price settles without needing
      // the user to touch a size pill first.
      if (anyZero && attempt < 5) {
        setTimeout(() => this.correctPricesFromRealWidget(tilesData, attempt + 1), 400);
      }
    }

    formatMoney(amount) {
      if (amount == null) return '';
      try {
        const currency = window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'EUR';
        const options = { style: 'currency', currency };
        // Czech prices are shown without decimals (999 Kč, not 999,00 Kč).
        if (currency === 'CZK') {
          options.minimumFractionDigits = 0;
          options.maximumFractionDigits = 0;
        }
        return new Intl.NumberFormat(document.documentElement.lang || undefined, options).format(amount);
      } catch (error) {
        return `${amount.toFixed(2)}`;
      }
    }

    t(template, tokens) {
      return Object.keys(tokens || {}).reduce(
        (text, key) => text.split(`{{ ${key} }}`).join(tokens[key]),
        template
      );
    }

    renderError(message) {
      if (this.tilesContainer) {
        this.tilesContainer.innerHTML = `<p class="kaching-custom-display__error">${message}</p>`;
      }
    }

    renderHeader(tilesData) {
      if (!this.headerContainer) return;
      const selected = tilesData.find((data) => data.tier.id === (this.selectedDealBarId || this.preselectedDealBarId))
        || tilesData[0];
      if (!selected) {
        this.headerContainer.hidden = true;
        return;
      }

      this.headerContainer.hidden = false;
      this.headerTitle.textContent = `${selected.tier.quantity}x ${this.productTitle}`;
      this.headerPriceCurrent.textContent = this.formatMoney(selected.price);

      const hasSavings = selected.comparePrice != null && selected.comparePrice > selected.price + 0.005;
      this.headerPriceCompare.hidden = !hasSavings;
      this.headerPriceCompare.textContent = hasSavings ? this.formatMoney(selected.comparePrice) : '';

      this.savingsBadge.hidden = !hasSavings;
      if (hasSavings) {
        this.savingsBadgeText.textContent = this.t(this.i18n.savingsPrefix, {
          amount: this.formatMoney(selected.comparePrice - selected.price),
        });
      }
    }

    renderTiles(tilesData) {
      this.tilesContainer.innerHTML = '';
      const maxQty = Math.max.apply(null, tilesData.map(function (t) { return t.tier.quantity || 0; }));
      // Katera plocica nosi ponudbo. Privzeto najvecja; nastavitev jo lahko
      // premakne na manjso (npr. 1+1 = kolicina 2), ce je ta glavna ponudba.
      const heroQtySetting = parseInt(this.dataset.heroTierQuantity || '', 10);
      const heroQty = heroQtySetting > 0 ? heroQtySetting : maxQty;
      // Kljukica gre na NAJMANJSI paket, ki doseze priporocene 3 mesece (90 dni).
      let protocolQty = null;
      tilesData.forEach(function (t) {
        const d = t.dosage && t.dosage.daysSupply;
        if (d && d >= 89 && (protocolQty === null || t.tier.quantity < protocolQty)) {
          protocolQty = t.tier.quantity;
        }
      });

      tilesData.forEach(({ tier, imageUrl, price, comparePrice, valid, validationMessage, dosage }, index) => {
        const position = index + 1;
        const badgeStyle = this.dataset[`tier${position}Style`] || 'none';
        const rawBadgeLabel = tier.label || tier.badgeText || '';
        const badgeLabel = this.translateFromKaching(rawBadgeLabel) || rawBadgeLabel;
        const isSelected = tier.id === (this.selectedDealBarId || this.preselectedDealBarId);

        const dosageLines = [];
        if (dosage) {
          const unitLabel = this.productType === 'powder' ? this.i18n.dosageUnitPowder : this.i18n.dosageUnitCapsules;
          dosageLines.push(
            this.t(this.i18n.dosageSupply, {
              months: dosage.monthsSupply,
              amount: dosage.totalCapsules,
              unit: unitLabel,
            })
          );
          dosageLines.push(
            this.t(this.i18n.pricePerDay, {
              amount: this.formatMoney(price / (dosage.daysSupply || 1)),
            })
          );
        } else if (tier.subtitle) {
          dosageLines.push(tier.subtitle);
        }

        const tileHtml = `
          <button
            type="button"
            class="kaching-tile${isSelected ? ' kaching-tile--selected' : ''}"
            ${valid ? '' : 'disabled'}
            data-deal-bar-id="${tier.id}"
          >
            ${imageUrl || !this.giftsInTier
              ? `<span class="kaching-tile__image" aria-hidden="true"${imageUrl ? ` style="background-image:url('${imageUrl}')"` : ''}></span>`
              : ''}
            <span class="kaching-tile__content">
              <span class="kaching-tile__title">${(this.giftsInTier && tier.quantity === heroQty && (this.dataset.heroTitle || '').trim()) ? this.dataset.heroTitle.trim() : (this.translateFromKaching(tier.title) || tier.title)}</span>
              <span class="kaching-tile__price-row">
                <span class="kaching-tile__price">${this.formatMoney(price / tier.quantity)}</span>
                ${this.giftsInTier && comparePrice && comparePrice > price
                  ? `<s class="kaching-tile__price-compare">${this.formatMoney(comparePrice / tier.quantity)}</s>`
                  : ''}
              </span>
              ${dosageLines.map((line) => `<span class="kaching-tile__dosage">${line}</span>`).join('')}
              ${this.giftsInTier && tier.quantity === protocolQty
                ? `<span class="kaching-tile__check">✓ ${this.dataset.protocolLabel || '3-mesečni protokol'}</span>`
                : ''}
              ${tier.quantity === 2 && (this.dataset.bfDuoGift || '').trim()
                ? `<span class="kaching-tile__checklist-item">${this.dataset.bfDuoGift.trim()}</span>`
                : ''}
              ${!valid ? `<span class="kaching-tile__unavailable">${validationMessage || this.i18n.unavailable}</span>` : ''}
            </span>
          </button>
        `;

        const isHero = this.giftsInTier && tier.quantity === heroQty;
        const heroLabel = isHero ? (this.dataset.heroLabel || '').trim() : '';
        const withGifts = isHero && !!this.gifts && !!this.gifts.length;
        const giftsHtml = withGifts ? this.giftsMarkup() : '';
        const listHtml = isHero ? this.checklistMarkup() : '';
        const label = heroLabel || badgeLabel;
        const bare = (badgeStyle === 'none' && !heroLabel) || (!label && !withGifts);

        let wrapperEl = document.createElement('div');
        if (bare) {
          wrapperEl.innerHTML = tileHtml;
        } else if ((badgeStyle === 'none' || !label) && !heroLabel) {
          wrapperEl.className = 'kaching-tile-wrapper';
          wrapperEl.innerHTML = `${tileHtml}${listHtml}${giftsHtml}`;
        } else {
          wrapperEl.className = `kaching-tile-wrapper kaching-tile-wrapper--${badgeStyle === 'none' ? 'best' : badgeStyle}`;
          wrapperEl.innerHTML = `
            <p class="kaching-tile-wrapper__label">${label}</p>
            ${tileHtml}
            ${listHtml}
            ${giftsHtml}
          `;
        }
        if (withGifts) wrapperEl.classList.add('kaching-tile-wrapper--has-gifts');

        const tileButton = wrapperEl.querySelector('.kaching-tile');
        tileButton.addEventListener('click', () => this.handleTileClick(tier));

        // Seznam in darila so zunaj gumba, zato klik nanju sam po sebi ne izbere
        // ponudbe. Poslusamo cel okvir; klike znotraj gumba pusti gumbu.
        if (isHero) {
          wrapperEl.classList.add('kaching-tile-wrapper--clickable');
          wrapperEl.addEventListener('click', (event) => {
            if (event.target.closest('.kaching-tile')) return;
            if (tileButton.disabled) return;
            this.handleTileClick(tier);
          });
        }

        if (bare) {
          this.tilesContainer.appendChild(tileButton);
        } else {
          this.tilesContainer.appendChild(wrapperEl);
        }
      });
    }

    // Darila izrisana kot del ploscice. Nastanejo znotraj renderTiles, zato
    // prezivijo vsak ponovni izris - nic se ne premika po DOM-u.
    // Vrednostni sklop, ki ga uporabnik vpise v urejevalniku teme.
    checklistMarkup() {
      let items = [];
      try {
        items = JSON.parse(this.dataset.offerChecklist || '[]');
      } catch (e) {
        items = [];
      }
      items = items.map((x) => String(x).trim()).filter(Boolean);
      const total = (this.dataset.offerTotal || '').trim();
      if (!items.length && !total) return '';
      const rows = items
        .map((x) => `<span class="kaching-tile__checklist-item">${x}</span>`)
        .join('');
      const totalRow = total ? `<span class="kaching-tile__total">${total}</span>` : '';
      return `<span class="kaching-tile__checklist">${rows}${totalRow}</span>`;
    }

    giftsMarkup() {
      if (!this.gifts || !this.gifts.length) return '';
      const heading = this.dataset.giftsTitle || '';
      const boxes = this.gifts
        .map((gift) => {
          const image = gift.image
            ? `<img class="kaching-gift__image" src="${gift.image}" alt="" loading="lazy">`
            : `<span class="kaching-gift__icon"><span class="material-icon material-symbols-outlined">${gift.icon || 'redeem'}</span></span>`;
          const priceEl = gift.comparePrice
            ? `<span class="kaching-gift__compare-price">${this.formatMoney(gift.comparePrice)}</span>`
            : '';
          return `<div class="kaching-gift kaching-gift--unlocked">${image}<span class="kaching-gift__title">${gift.title}</span>${priceEl}</div>`;
        })
        .join('');
      return `<div class="kaching-tile__gifts">${heading ? `<p class="kaching-tile__gifts-heading">${heading}</p>` : ''}<div class="kaching-tile__gifts-list">${boxes}</div></div>`;
    }

    renderGifts() {
      if (!this.giftsContainer) return;

      if (!this.gifts || this.gifts.length === 0) {
        this.giftsContainer.innerHTML = '';
        if (this.giftsWrapper) this.giftsWrapper.hidden = true;
        return;
      }

      if (this.giftsWrapper) this.giftsWrapper.hidden = !!this.giftsInTier;
      if (this.giftsInTier) return;

      const headingEl = this.querySelector('.kaching-custom-display__gifts-heading');
      if (headingEl && this.giftsTitle) {
        headingEl.textContent = this.translateFromKaching(this.giftsTitle) || this.giftsTitle;
      }

      const selectedBarPosition = this.getSelectedBarPosition();
      const visibleGifts = this.hideLockedGifts
        ? this.gifts.filter((gift) => selectedBarPosition >= gift.unlockAtBar)
        : this.gifts;

      this.giftsContainer.innerHTML = visibleGifts
        .map((gift) => {
          const unlocked = selectedBarPosition >= gift.unlockAtBar;
          const image = gift.image
            ? `<img class="kaching-gift__image" src="${gift.image}" alt="" loading="lazy">`
            : `<span class="kaching-gift__icon"><span class="material-icon material-symbols-outlined">${gift.icon || 'redeem'}</span></span>`;
          const title = unlocked
            ? `<span class="kaching-gift__title">${gift.title}</span>`
            : `<span class="kaching-gift__title">${gift.lockedTitle || 'Locked'}</span>`;
          const priceEl = gift.comparePrice
            ? `<span class="kaching-gift__compare-price">${this.formatMoney(gift.comparePrice)}</span>`
            : '';

          return `
            <div class="kaching-gift${unlocked ? ' kaching-gift--unlocked' : ''}">
              ${image}
              ${title}
              ${priceEl}
            </div>
          `;
        })
        .join('');
    }

    async handleTileClick(tier) {
      this.selectedDealBarId = tier.id;
      this.refresh();

      // Only sync the selection into Kaching's own widget so its internal
      // state (and therefore the real Add to Cart button) reflects the
      // chosen tier. Adding to cart itself is left entirely to the
      // visible Add to Cart button - we never submit on the user's behalf.
      const kachingBlock = await this.waitForKachingBlock();
      if (kachingBlock) {
        const radio = kachingBlock.querySelector(
          `[data-deal-bar-id="${tier.id}"] input[type="radio"]`
        );
        if (radio && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('input', { bubbles: true }));
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          radio.dispatchEvent(new Event('click', { bubbles: true }));
        } else if (!radio) {
          console.warn('kaching-custom-display: no matching deal-bar radio found for', tier.id);
        }
      } else {
        console.warn('kaching-custom-display: kaching-bundles-block never appeared.');
      }
    }
  }

  customElements.define('kaching-custom-display', KachingCustomDisplay);
}
